import React, { useEffect, useMemo, useRef, useState } from "react";
import Modal from "react-bootstrap/Modal";
import { useQuery, useQueryClient } from "react-query";
import { schedulingQuery } from "../../../../lib/schedulingCache";
import { useFormik } from "formik";
import moment from "moment-timezone";
import { Button } from "../../../common/Button";
import TextInput from "../../../common/textfield/TextInput";
import SelectFiled from "../../../common/textfield/SelectFiled";
import SearchSelect from "../../../common/textfield/SearchSelect";
import SearchIcon from "@mui/icons-material/Search";
import { components as selectComponents, createFilter } from "react-select";
import { failed, success } from "../../../common/Toastify";
import { useSchedulingOrg } from "../../../../hooks/ReactQueryHooks/useSchedulingOrg";
import { useServiceCategoriesQuery } from "../../../../hooks/ReactQueryHooks/useServiceCategoriesQuery";
import { useSchedulesQuery } from "../../../../hooks/ReactQueryHooks/useSchedulesQuery";
import { listAllAvailableSlots, listAvailableDates } from "../../../api/Scheduling/slots";
import { extractHold, getHold, placeHold, placeNoPreferenceHold, releaseHold } from "../../../api/Scheduling/holds";
import { appointmentHasLinkedEncounter } from "../appointments/appointmentEncounter";
import { confirmAppointment, rescheduleAppointment } from "../../../api/Scheduling/appointments";
import { getPatientData } from "../../../api/Individual";
import { schedulingErrorMessage } from "../../../api/Scheduling/errors";
import { formatInZone, formatRange } from "../time";
import { displayPractitionerName, loadPractitionerName } from "../names";
import AvailabilityDatePicker from "./AvailabilityDatePicker";
import BookingPhoneInput from "./BookingPhoneInput";
import AppointmentVisitDetails, { AppointmentVisitEditor } from "./AppointmentVisitDetails";
import { isValidPhoneNumber, parseBookingPhone } from "./phoneNumber";
import { NOTIFICATION_DESTINATIONS, notificationDestinationHelp, patientProfilePhone } from "./notificationDestination";
import "../Scheduling.css";
import TimeZoneSelect from "../TimeZoneSelect";
import { deviceTimeZone, normalizeTimeZone, timeZoneLabel } from "../timeZones";
import OrganizationSelect from "../OrganizationSelect";
import { getEncounterRecord } from "../../../apps/api/Encounter";

function organizationFromEncounter(record) {
  return record?.orgId
    || record?.serviceProvider?.reference?.replace(/^Organization\//, "")
    || record?.managingOrganization?.reference?.replace(/^Organization\//, "")
    || "";
}

const BookingModal = (props) => {
  const context = useSchedulingOrg();
  const sourceEncounterId = props.appointment?.bookingContext?.sourceEncounterId || props.encounterId;
  const source = useQuery(["booking-source-encounter", sourceEncounterId], async () => {
    const payload = (await getEncounterRecord({ encounterId: sourceEncounterId }))?.data;
    return payload?.id ? payload : payload?.data;
  }, { enabled: !!props.modalShow && !!sourceEncounterId, retry: false, refetchOnWindowFocus: false });
  const sourceOrgId = props.appointment?.organizationRef || organizationFromEncounter(source.data) || props.orgId || context.lockedOrgId || context.organizationRef;
  const sourceOrgName = source.data?.serviceProvider?.display || source.data?.managingOrganization?.display
    || context.orgOptions?.find(org => org.value === sourceOrgId)?.label || context.organizationName || "Organization";
  const orgOptions = [...(context.orgOptions || [])];
  if (sourceOrgId && !orgOptions.some(org => org.value === sourceOrgId)) orgOptions.unshift({ value: sourceOrgId, label: sourceOrgName });
  const organizationControl = () => <div className="booking-organization-fields">
    <OrganizationSelect context={context} inputId="booking-organization" value={sourceOrgId}
      options={orgOptions.length ? orgOptions : [{ value: sourceOrgId, label: sourceOrgName }]}
      disabled onChange={() => {}}
      description={props.appointment ? "Organization of this appointment." : "Organization of the source encounter."} />
  </div>;
  return <BookingForm key={`${sourceOrgId}:${sourceEncounterId || ""}`} {...props} orgId={sourceOrgId}
    encounterId={sourceEncounterId} organizationControl={organizationControl} />;
};

const EMPTY_SCHEDULES = [];


const BookingSearchControl = ({ children, ...props }) => (
  <selectComponents.Control {...props}>
    <SearchIcon className="booking-search-icon" aria-hidden="true" />
    {children}
  </selectComponents.Control>
);
const bookingSelectComponents = { Control: BookingSearchControl };
const NO_PREFERENCE_OPTION = { label: "No preference", value: "none" };
const practitionerLabelFilter = createFilter({ stringify: (option) => String(option.label || "") });
const practitionerFilterOption = (option, input) => option.data?.value === "none" || practitionerLabelFilter(option, input);

const BookingForm = ({
  modalShow,
  onHide,
  type = "intake",
  isInternalReferral = false,
  onSubmitOverride,
  patientId,
  patientName,
  encounterId,
  orgId,
  appointment,
  appointmentEncounter,
  encounterLoading = false,
  encounterError = "",
  onEncounterReload,
  appointmentPractitionerNames = [],
  preferredPractitionerId,
  intakePrefill,
  referralContext,
  onSuccess,
  organizationControl,
}) => {
  const queryClient = useQueryClient();
  const { organizationRef, organizationTimeZone, orgOptions } = useSchedulingOrg();
  const resolvedOrgId = appointment?.organizationRef || orgId || organizationRef;
  const bookingOrganizationZone = resolvedOrgId === organizationRef ? organizationTimeZone :
    normalizeTimeZone(orgOptions?.find((org) => org.value === resolvedOrgId)?.timeZone);
  const isReschedule = type === "reschedule";
  const hasLinkedEncounter = appointmentHasLinkedEncounter(appointment, appointmentEncounter);
  const localZone = deviceTimeZone();
  const defaultBookingZone = bookingOrganizationZone || "UTC";
  const [bookingZone, setBookingZone] = useState(defaultBookingZone);
  const [bookingPreference, setBookingPreference] = useState(defaultBookingZone);
  const bookingZoneTouched = useRef(false);
  const { data: categories = [] } = useServiceCategoriesQuery({
    organizationRef: resolvedOrgId,
    active: isReschedule ? undefined : true,
    enabled: !!modalShow,
  });
  const [practitioners, setPractitioners] = useState([]);
  const [availableDates, setAvailableDates] = useState([]);
  const [datesLoading, setDatesLoading] = useState(false);
  const [slots, setSlots] = useState([]);
  const [month, setMonth] = useState(() => moment.max(
    moment.tz(bookingZone).startOf("month"),
    isReschedule && appointment?.startsAt ? moment(appointment.startsAt).tz(bookingZone).startOf("month") : moment.tz(bookingZone).startOf("month")
  ).format("YYYY-MM-DD"));
  const [selectedDate, setSelectedDate] = useState("");
  const [slotsError, setSlotsError] = useState("");
  const [slotsRetry, setSlotsRetry] = useState(0);
  const [holdLoading, setHoldLoading] = useState(false);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [hold, setHold] = useState(null);
  const [holdSeconds, setHoldSeconds] = useState(0);
  const [btnLoading, setBtnLoading] = useState(false);
  const [editingVisitDetails, setEditingVisitDetails] = useState("");
  const [saveError, setSaveError] = useState("");
  const [pendingBooking, setPendingBooking] = useState(null);
  const [patientPhone, setPatientPhone] = useState();
  useEffect(() => {
    if (type === "reschedule" || !patientId) {
      setPatientPhone(undefined);
      return undefined;
    }
    let active = true;
    setPatientPhone(undefined);
    getPatientData(patientId).then((payload) => {
      if (active) setPatientPhone(patientProfilePhone(payload));
    }).catch(() => {
      if (active) setPatientPhone(undefined);
    });
    return () => { active = false; };
  }, [patientId, type]);
  const submitInFlight = useRef(false);
  const holdRef = useRef(null);
  const slotAfterZoneChange = useRef(null);

  useEffect(() => {
    if (bookingZoneTouched.current || !bookingOrganizationZone || bookingZone === bookingOrganizationZone) return;
    setBookingZone(bookingOrganizationZone);
    setBookingPreference(bookingOrganizationZone);
    setMonth(moment.max(
      moment.tz(bookingOrganizationZone).startOf("month"),
      isReschedule && appointment?.startsAt
        ? moment(appointment.startsAt).tz(bookingOrganizationZone).startOf("month")
        : moment.tz(bookingOrganizationZone).startOf("month")
    ).format("YYYY-MM-DD"));
  }, [appointment?.startsAt, bookingOrganizationZone, bookingZone, isReschedule]);

  const title =
    type === "reschedule"
      ? `Reschedule Appointment for ${patientName || "patient"}`
      : isInternalReferral
        ? "Internal Referral - Book An Appointment"
        : `Create Appointment for ${patientName || "patient"}`;

  const formik = useFormik({
    initialValues: {
      serviceCategoryId: isReschedule ? appointment?.serviceCategoryId || "" : "",
      preference: isReschedule || preferredPractitionerId ? "practitioner" : "none",
      practitionerRef: isReschedule ? appointment?.practitionerRefs?.[0] || "" : preferredPractitionerId || "",
      practitionerRefs: isReschedule ? appointment?.practitionerRefs || [] : [],
      selectedSlot: "",
      reason: intakePrefill?.reason || "",
      preferredChannel: intakePrefill?.channel || "phone",
      smsNotificationDestination: "patient_phone",
      ...parseBookingPhone(intakePrefill?.phone, intakePrefill?.ext),
    },
    enableReinitialize: false,
    validate: (values) => {
      if (type === "reschedule") return {};
      if (!values.phone) return { phone: "Enter a return phone number." };
      return isValidPhoneNumber(values.phone) ? {} : { phone: "Enter a valid phone number." };
    },
    onSubmit: async () => {},
  });

  const selectedCategoryId = formik.values.serviceCategoryId;
  const selectedPractitionerKey = formik.values.practitionerRefs.join(",");
  const selectedPractitionerRef = isReschedule
    ? formik.values.practitionerRefs.length === 1 ? formik.values.practitionerRefs[0] : null
    : formik.values.preference === "practitioner" ? formik.values.practitionerRef : null;
  const { data: scheduleRows, isFetching: practitionersLoading, isError: suppliersError } = useSchedulesQuery({
    organizationRef: resolvedOrgId, scope: "supply", serviceCategoryId: selectedCategoryId,
    enabled: !!modalShow && !!selectedCategoryId,
  });
  const offeringSchedules = scheduleRows || EMPTY_SCHEDULES;
  const supplierKey = JSON.stringify(offeringSchedules);
  const selectedRefsKey = [...new Set([formik.values.practitionerRef, ...formik.values.practitionerRefs].filter(Boolean))].sort().join(",");
  useEffect(() => {
    let active = true;
    const suppliers = new Map();
    const supplierRows = JSON.parse(supplierKey);
    supplierRows.forEach(row => {
      const previous = suppliers.get(row.practitionerRef);
      const organization = row.organizationName || row.organizationRef;
      suppliers.set(row.practitionerRef, {
        value: row.practitionerRef,
        name: row.practitionerName ? displayPractitionerName({ name: row.practitionerName }) : previous?.name || "Practitioner",
        organizations: [...new Set([...(previous?.organizations || []), organization].filter(Boolean))],
      });
    });
    setPractitioners([...suppliers.values()]);
    const refs = [...new Set([...(selectedRefsKey ? selectedRefsKey.split(",") : []), ...supplierRows.filter(row => !row.practitionerName).map(row => row.practitionerRef)])];
    // Identity and eligible choices are usable even when optional name lookup fails.
    Promise.all(refs.slice(0, 20).map(async ref => ({ value: ref, name: await loadPractitionerName(ref) }))).then(selected => {
      if (!active) return;
      selected.forEach(option => {
        const existing = suppliers.get(option.value);
        if (!existing) suppliers.set(option.value, { ...option, historical: true });
        else if (existing.name === "Practitioner") suppliers.set(option.value, { ...existing, name: option.name });
      });
      setPractitioners([...suppliers.values()]);
    });
    return () => { active = false; };
  }, [selectedRefsKey, supplierKey, resolvedOrgId]);

  useEffect(() => {
    holdRef.current = hold;
  }, [hold]);

  useEffect(() => {
    if (!hold?.expiresAt) return undefined;
    const tick = () => {
      const remaining = Math.max(0, Math.round((new Date(hold.expiresAt).getTime() - Date.now()) / 1000));
      setHoldSeconds(remaining);
      if (remaining <= 0) {
        holdRef.current = null;
        setHold(null);
      }
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [hold]);

  useEffect(() => {
    return () => {
      const current = holdRef.current;
      if (current?.holdId) {
        releaseHold({ organizationRef: resolvedOrgId, holdId: current.holdId }).catch(() => {});
      }
    };
  }, [resolvedOrgId]);

  const selectedCategory = categories.find((category) => category.serviceCategoryId === formik.values.serviceCategoryId);
  const categorySelectOptions = useMemo(
    () =>
      categories.map((category) => ({
        label: `${category.name} (${category.durationMinutes} min)`,
        value: category.serviceCategoryId,
      })),
    [categories]
  );
  const isMultiPractitioner = isReschedule && appointment?.practitionerRefs?.length > 1;
  const practitionerOptions = [
    NO_PREFERENCE_OPTION,
    ...practitioners.map((person) => ({ label: `${person.name}${offeringSchedules.some(row => row.practitionerRef === person.value && row.organizationRef && row.organizationRef !== resolvedOrgId) ? " · Parent" : ""}${person.organizations?.length ? ` · ${person.organizations.join(", ")}` : ""}`, value: person.value, isDisabled: !!person.historical })),
  ];
  const parsedSlot = useMemo(() => {
    if (!formik.values.selectedSlot) return null;
    try {
      return JSON.parse(formik.values.selectedSlot);
    } catch {
      return null;
    }
  }, [formik.values.selectedSlot]);
  const practitionerSchedules = offeringSchedules.filter((row) => selectedPractitionerRef && row.practitionerRef === selectedPractitionerRef);
  const selectedSchedule = practitionerSchedules.find((row) => row.scheduleId === parsedSlot?.scheduleId || parsedSlot?.scheduleIds?.includes(row.scheduleId));
  const practitionerZones = [...new Set(practitionerSchedules.map((row) => normalizeTimeZone(row.timezoneName)).filter(Boolean))];
  const practitionerZone = normalizeTimeZone(selectedSchedule?.timezoneName) || (practitionerZones.length === 1 ? practitionerZones[0] : undefined);
  const noPreference = formik.values.preference === "none";
  const rescheduleChanged = isReschedule && parsedSlot && (
    noPreference ||
    !moment(parsedSlot.startsAt).isSame(appointment.startsAt) || !moment(parsedSlot.endsAt).isSame(appointment.endsAt) ||
    [...(parsedSlot.scheduleIds || [])].sort().join(",") !== [...(appointment.scheduleIds || [])].sort().join(",")
  );
  const canSearch = !!modalShow && !!selectedCategoryId && (noPreference || (isReschedule ? !!selectedPractitionerKey : !!formik.values.practitionerRef));
  useEffect(() => {
    const controller = new AbortController();
    setAvailableDates([]); setSelectedDate(""); setSlots([]); setSlotsError(""); setSaveError("");
    formik.setFieldValue("selectedSlot", "");
    if (!canSearch) { setDatesLoading(false); return undefined; }
    setDatesLoading(true);
    (async () => {
      const dates = [];
      const end = moment.tz(month, bookingZone).add(1,"month").startOf("month");
      let from = moment.max(moment.tz(month,bookingZone).startOf("month"),moment.tz(bookingZone).startOf("day"));
      while (from.isBefore(end)) {
        const to = moment.min(from.clone().add(6,"days"),end);
        const params = { organizationRef: resolvedOrgId, serviceCategoryId: selectedCategoryId, startsAt: from.toISOString(), endsAt: to.toISOString(),
          datesTimezone: bookingZone, ...(isReschedule && !noPreference ? { excludeAppointmentId: appointment.appointmentId } : {}),
          ...(noPreference ? {} : isReschedule ? { practitionerRefs: selectedPractitionerKey.split(",") } : { practitionerRef: formik.values.practitionerRef }) };
        const rows = await queryClient.fetchQuery(["scheduling-available-dates", params, noPreference], schedulingQuery(({ consistency }) => listAvailableDates(params,noPreference,controller.signal,consistency)), { staleTime: 30000, retry: false });
        if (controller.signal.aborted) return;
        dates.push(...rows.map(slot=>formatInZone(slot.startsAt,bookingZone,"YYYY-MM-DD")));
        from=to;
      }
      const unique=[...new Set(dates)].sort();
      setAvailableDates(unique);
      const preferred = slotAfterZoneChange.current?.startsAt || (isReschedule ? appointment.startsAt : null);
      const preferredDay=preferred ? formatInZone(preferred,bookingZone,"YYYY-MM-DD") : "";
      setSelectedDate(unique.includes(preferredDay) ? preferredDay : unique[0] || "");
    })().catch(error => { if (!controller.signal.aborted) setSlotsError(schedulingErrorMessage(error,"Could not load available dates.")); })
      .finally(()=>{ if(!controller.signal.aborted)setDatesLoading(false); });
    return ()=>controller.abort();
    // Stable search values deliberately exclude Formik's changing object identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canSearch, resolvedOrgId, selectedCategoryId, selectedPractitionerKey, formik.values.practitionerRef, noPreference, month, bookingZone, slotsRetry]);
  useEffect(() => {
    const controller = new AbortController();
    setSlots([]); formik.setFieldValue("selectedSlot", "");
    if (!canSearch || !selectedDate) { setSlotsLoading(false); return undefined; }
    setSlotsLoading(true); setSlotsError("");
    const from=moment.tz(selectedDate,bookingZone).startOf("day");
    const refs=noPreference ? [undefined] : isReschedule ? selectedPractitionerKey.split(",") : [formik.values.practitionerRef];
    (async()=> {
      const groups=[];
      // Sequential group reads bound concurrent load; only the selected day is fetched.
      for (const ref of refs) {
        if(controller.signal.aborted)return;
        const params={ organizationRef:resolvedOrgId,serviceCategoryId:selectedCategoryId,practitionerRef:ref,startsAt:from.toISOString(),endsAt:from.clone().add(1,"day").toISOString(),
          ...(isReschedule && !noPreference ? {excludeAppointmentId:appointment.appointmentId}: {}) };
        groups.push(await queryClient.fetchQuery(["scheduling-day-slots",params,noPreference],schedulingQuery(({ consistency })=>listAllAvailableSlots({...params,signal:controller.signal,consistency},noPreference)),{staleTime:15000,retry:false}));
      }
      if(controller.signal.aborted)return;
      const eligible=groups.map(group=>isReschedule?[...group].sort((a,b)=>Number(appointment.scheduleIds?.includes(b.scheduleId))-Number(appointment.scheduleIds?.includes(a.scheduleId))):group);
      const results=(eligible[0]||[]).filter(slot=>eligible.every(group=>group.some(candidate=>candidate.startsAt===slot.startsAt&&candidate.endsAt===slot.endsAt)))
        .map(slot=>isReschedule?{...slot,scheduleIds:eligible.map(group=>group.find(candidate=>candidate.startsAt===slot.startsAt&&candidate.endsAt===slot.endsAt).scheduleId)}:slot);
      const unique=[...new Map(results.map(slot=>[JSON.stringify([slot.startsAt,slot.endsAt,isReschedule?null:slot.scheduleId]),slot])).values()].filter(slot=>moment(slot.startsAt).isAfter(moment())).sort((a,b)=>a.startsAt.localeCompare(b.startsAt));
      setSlots(unique);
      const prior=slotAfterZoneChange.current;
      slotAfterZoneChange.current=null;
      const restored=prior ? unique.find(slot=>slot.startsAt===prior.startsAt&&slot.endsAt===prior.endsAt&&(isReschedule||slot.scheduleId===prior.scheduleId)) : isReschedule && !noPreference ? unique.find(slot=>moment(slot.startsAt).isSame(appointment.startsAt)&&moment(slot.endsAt).isSame(appointment.endsAt)) : null;
      if(restored)formik.setFieldValue("selectedSlot",JSON.stringify(restored));
    })().catch(error=>{if(!controller.signal.aborted)setSlotsError(schedulingErrorMessage(error,"Could not load available times."));})
      .finally(()=>{if(!controller.signal.aborted)setSlotsLoading(false);});
    return ()=>controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canSearch, selectedDate, resolvedOrgId, selectedCategoryId, selectedPractitionerKey, formik.values.practitionerRef, noPreference, bookingZone, slotsRetry]);

  const daySlots = slots.filter((slot) => formatInZone(slot.startsAt, bookingZone, "YYYY-MM-DD") === selectedDate);

  const dropHold = async () => {
    if (!hold?.holdId) return;
    await releaseHold({ organizationRef: resolvedOrgId, holdId: hold.holdId }).catch(() => {});
    holdRef.current = null;
    setHold(null);
  };

  const takeHold = async () => {
    if (!parsedSlot) {
      failed("Select a slot.");
      return null;
    }
    setHoldLoading(true);
    await dropHold();
    const noPreference = formik.values.preference === "none";
    const body = {
      organizationRef: resolvedOrgId,
      serviceCategoryId: formik.values.serviceCategoryId,
      startsAt: parsedSlot.startsAt,
      endsAt: parsedSlot.endsAt,
      patientRef: patientId || undefined,
      ttlSeconds: 300,
    };
    try {
      const created = noPreference
        ? await placeNoPreferenceHold(body)
        : await placeHold({
            ...body,
            scheduleIds: parsedSlot.scheduleId ? [parsedSlot.scheduleId] : undefined,
          });
      const holdId = created?.holdId;
      const full = await getHold({ organizationRef: resolvedOrgId, holdId });
      const nextHold = extractHold(full, holdId) || {
        holdId,
        expiresAt: moment().add(300, "seconds").toISOString(),
      };
      holdRef.current = nextHold;
      setHold(nextHold);
      return nextHold;
    } catch (error) {
      failed(schedulingErrorMessage(error));
      return null;
    } finally {
      setHoldLoading(false);
    }
  };

  const buildBookingContext = () => ({
    sourceEncounterId: encounterId,
    reason: formik.values.reason.trim(),
    preferredChannel: formik.values.preferredChannel,
    smsNotificationDestination: formik.values.smsNotificationDestination,
    phone: formik.values.phone,
    ext: formik.values.ext,
    ...(referralContext ? { internalReferral: referralContext } : {}),
  });

  const submitBooking = async (blockOriginal) => {
    const shouldBlockOriginal = isReschedule && blockOriginal === true;
    if (submitInFlight.current || (isReschedule && (!rescheduleChanged || hasLinkedEncounter))) return;
    if (!pendingBooking && type !== "reschedule" && !formik.values.reason.trim()) {
      failed("Reason is required.");
      return;
    }
    if (!pendingBooking && type !== "reschedule") {
      const errors = await formik.validateForm();
      if (errors.phone) {
        formik.setFieldTouched("phone", true, false);
        return;
      }
    }
    if (!parsedSlot && !pendingBooking) {
      failed("Select a slot.");
      return;
    }
    submitInFlight.current = true;
    setBtnLoading(true);
    setSaveError("");
    let bookingAttempt = pendingBooking;
    try {
      if (type === "reschedule" && appointment) {
        let scheduleIds = parsedSlot.scheduleIds;
        if (noPreference) {
          const assigned = await listAllAvailableSlots({
            organizationRef: resolvedOrgId,
            serviceCategoryId: formik.values.serviceCategoryId,
            startsAt: parsedSlot.startsAt,
            endsAt: parsedSlot.endsAt,
          });
          const match = assigned.find((slot) => slot.startsAt === parsedSlot.startsAt && slot.endsAt === parsedSlot.endsAt);
          if (!match?.scheduleId) {
            setSaveError("The selected time is no longer available.");
            return;
          }
          scheduleIds = [match.scheduleId];
        }
        await rescheduleAppointment({
          organizationRef: resolvedOrgId,
          appointmentId: appointment.appointmentId,
          startsAt: parsedSlot.startsAt,
          endsAt: parsedSlot.endsAt,
          scheduleIds,
          version: appointment.version,
          ...(shouldBlockOriginal ? { blockOriginal: true } : {}),
        });
        if (shouldBlockOriginal) {
          await queryClient.invalidateQueries(["scheduling-blocks"]);
        }
        success(shouldBlockOriginal ? "Appointment rescheduled and original time blocked" : "Appointment Rescheduled");
        onSuccess?.();
        onHide();
        return;
      }

      if (!encounterId) throw new Error("A source encounter is required to book an appointment.");
      const activeHold = pendingBooking || (hold?.holdId ? hold : await takeHold());
      if (!activeHold?.holdId) {
        failed("Could not hold the slot. Try again.");
        return;
      }

      const bookingContext = pendingBooking?.bookingContext || buildBookingContext();
      const bookingPayload = pendingBooking || {
        organizationRef: resolvedOrgId,
        holdId: activeHold.holdId,
        appointmentStatus: "booked",
        startsAt: parsedSlot.startsAt,
        endsAt: parsedSlot.endsAt,
        serviceCategoryId: formik.values.serviceCategoryId,
        practitionerRefs: parsedSlot.practitionerRef ? [parsedSlot.practitionerRef] : activeHold.practitionerRefs,
        scheduleIds: parsedSlot.scheduleId ? [parsedSlot.scheduleId] : activeHold.scheduleIds,
        patientRefs: patientId ? [patientId] : [],
        sourceEncounterId: encounterId,
        bookingContext,
        reason: formik.values.reason,
        preferredChannel: formik.values.preferredChannel,
        phone: formik.values.phone,
        ext: formik.values.ext,
      };
      bookingAttempt = bookingPayload;

      if (onSubmitOverride) {
        await onSubmitOverride(bookingPayload);
        success("Appointment Scheduled");
        onSuccess?.();
        holdRef.current = null;
        setHold(null);
        onHide();
        return;
      }

      const created = await confirmAppointment({
        organizationRef: resolvedOrgId,
        holdId: activeHold.holdId,
        patientRefs: bookingPayload.patientRefs,
        appointmentStatus: "booked",
        bookingContext,
      });
      const appointmentId = created?.appointmentId;
      success("Appointment Scheduled");
      onSuccess?.({ appointmentId, sourceEncounterId: encounterId });
      holdRef.current = null;
      setHold(null);
      onHide();
    } catch (error) {
      if (error.referralAborted) {
        setPendingBooking(null);
        holdRef.current = null;
        setHold(null);
        formik.setFieldValue("selectedSlot", "");
        failed(schedulingErrorMessage(error));
      } else if (error.bookedAppointment || error.referralRecovery || pendingBooking) {
        setPendingBooking(bookingAttempt);
        holdRef.current = null;
        setHold(null);
        setSaveError(error.referralRecovery
          ? "The booking needs to be completed. Retry to check the appointment and save its referral."
          : "Appointment booked. Retry linking it to the source encounter.");
      } else {
        if (isReschedule) setSaveError(schedulingErrorMessage(error));
        else failed(schedulingErrorMessage(error));
        await dropHold();
      }
    } finally {
      submitInFlight.current = false;
      setBtnLoading(false);
    }
  };

  const handleClose = async () => {
    await dropHold();
    onHide();
  };

  const selectionLocked = !!hold || !!pendingBooking || btnLoading || holdLoading;
  const changeBookingZone = (preference) => {
    const nextZone = preference === "local" ? localZone : preference;
    bookingZoneTouched.current = true;
    setBookingPreference(preference);
    if (nextZone === bookingZone) return;
    slotAfterZoneChange.current = parsedSlot;
    setBookingZone(nextZone);
    setMonth(moment.max(moment.tz(nextZone).startOf("month"),
      moment.tz(parsedSlot?.startsAt || appointment?.startsAt || `${month}T12:00:00`, bookingZone)
        .tz(nextZone).startOf("month")).format("YYYY-MM-DD"));
  };
  return (
    <>
    <Modal show={modalShow && !editingVisitDetails} onHide={selectionLocked ? undefined : handleClose} size="xl" centered backdrop="static" className="custom-dialog scheduling-dialog booking-dialog">
      <Modal.Header closeButton={!selectionLocked}>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <form className="common-form border-fields schedule-booking-form" onSubmit={(event) => event.preventDefault()}>
          <fieldset disabled={selectionLocked} style={{ minWidth: 0 }}>
            <div className="schedule-booking-context">
            {organizationControl?.(selectionLocked)}
            <div className="schedule-booking-zone"><TimeZoneSelect
              value={bookingPreference === "local" ? "local" : bookingZone}
              onChange={changeBookingZone} localZone={localZone}
              practitionerZone={practitionerZone} organizationZone={bookingOrganizationZone} disabled={selectionLocked} date={selectedDate || month} /></div>
            </div>
            {isReschedule ? <section className="schedule-reschedule-current" aria-labelledby="current-appointment-title">
              <h3 id="current-appointment-title">Current appointment</h3>
              <p>{formatRange(appointment.startsAt, appointment.endsAt, bookingZone)}</p>
              <dl className="appointment-detail-fields">
                <div><dt>Appointment type</dt><dd>{selectedCategory?.name || "Appointment type unavailable"}</dd></div>
                <div><dt>Practitioner{appointmentPractitionerNames.length > 1 ? "s" : ""}</dt><dd>{appointmentPractitionerNames.join(", ") || practitioners.find((person) => person.value === formik.values.practitionerRef)?.name || "Loading…"}</dd></div>
              </dl>
            </section> : <div className="schedule-field-grid schedule-booking-search">
              <SearchSelect keyField="serviceCategoryId" label="Appointment type" aria-label="Appointment type" required formik={formik}
                components={bookingSelectComponents}
                inputId="booking-appointment-type" placeholder="Search appointment types…" options={categorySelectOptions} isDisabled={selectionLocked}
                noOptionsMessage={() => "No matching appointment types"}
                value={categorySelectOptions.find((option) => option.value === selectedCategoryId) || null}
                onChange={(selected) => {
                  if (selectionLocked) return;
                  formik.setFieldValue("serviceCategoryId", selected?.value || "");
                  formik.setFieldValue("practitionerRef", "");
                  formik.setFieldValue("preference", "none");
                  formik.setFieldValue("selectedSlot", "");
                }} />
              <SearchSelect keyField="practitionerRef" inputId="booking-practitioner" label="Practitioner" aria-label="Practitioner"
                components={bookingSelectComponents} filterOption={practitionerFilterOption}
                placeholder="Search practitioners…" options={practitionerOptions} isDisabled={!selectedCategoryId || selectionLocked}
                isLoading={practitionersLoading}
                noOptionsMessage={() => suppliersError ? "Unable to load practitioners" : "No eligible practitioners for this appointment type"}
                value={noPreference ? NO_PREFERENCE_OPTION : practitionerOptions.find((option) => option.value === formik.values.practitionerRef) || null}
                onChange={(selected) => {
                  const none = !selected?.value || selected.value === "none";
                  formik.setFieldValue("practitionerRef", none ? "" : selected.value);
                  formik.setFieldValue("preference", none ? "none" : "practitioner");
                  formik.setFieldValue("selectedSlot", "");
                }} />
            </div>}
            {isReschedule && <div className="schedule-booking-search schedule-reschedule-practitioner">
              <SearchSelect label={isMultiPractitioner && !noPreference ? "Practitioners" : "Practitioner"} aria-label="Practitioner"
                inputId="reschedule-practitioner" components={bookingSelectComponents} filterOption={practitionerFilterOption}
                options={practitionerOptions} placeholder="Search practitioners…"
                isMulti={isMultiPractitioner && !noPreference} isClearable={false}
                isDisabled={selectionLocked} isLoading={practitionersLoading}
                noOptionsMessage={() => suppliersError ? "Unable to load practitioners" : "No eligible practitioners for this appointment type"}
                value={noPreference ? NO_PREFERENCE_OPTION : isMultiPractitioner ? practitionerOptions.filter((option) => formik.values.practitionerRefs.includes(option.value)) : practitionerOptions.find((option) => option.value === formik.values.practitionerRefs[0]) || null}
                onChange={(selected) => {
                  formik.setFieldValue("selectedSlot", "");
                  if (Array.isArray(selected)) {
                    if (selected.some((option) => option.value === "none")) {
                      formik.setFieldValue("preference", "none");
                      formik.setFieldValue("practitionerRef", "");
                      formik.setFieldValue("practitionerRefs", []);
                      return;
                    }
                    if (selected.length > 20) { failed("Select at most 20 practitioners."); return; }
                    formik.setFieldValue("preference", "practitioner");
                    formik.setFieldValue("practitionerRefs", selected.map((option) => option.value));
                    return;
                  }
                  const none = !selected?.value || selected.value === "none";
                  formik.setFieldValue("preference", none ? "none" : "practitioner");
                  formik.setFieldValue("practitionerRef", none ? "" : selected.value);
                  formik.setFieldValue("practitionerRefs", none ? [] : [selected.value]);
                }} />
            </div>}
            <div className="schedule-booking-layout">
              <AvailabilityDatePicker month={month} onMonthChange={setMonth} availableDates={availableDates} selectedDate={selectedDate}
                timezoneName={bookingZone}
                onSelect={(date) => { setSelectedDate(date); formik.setFieldValue("selectedSlot", ""); }} loading={datesLoading} disabled={selectionLocked || !selectedCategoryId} />
              <div className="schedule-booking-times" aria-busy={slotsLoading}>
                <h3>{selectedDate ? moment.utc(selectedDate).format("dddd, MMMM D") : "Choose an available day"}</h3>
                {selectedCategory && <p className="schedule-help">{selectedCategory.durationMinutes} minute appointments</p>}
                {slotsError ? <div className="schedule-error" role="alert">{slotsError}<button type="button" className="schedule-text-button" onClick={() => setSlotsRetry((value) => value + 1)}>Try again</button></div> : null}
                {!datesLoading && !slotsLoading && selectedCategoryId && !slots.length && !slotsError && <p className="scheduling-empty mt-3">{selectedDate ? "No times remain on this day. Choose another date." : "No available appointments this month. Try another month or practitioner."}</p>}
                {slotsLoading && <p role="status">Loading available times…</p>}
                <div className="schedule-slot-grid">
                  {!slotsLoading && daySlots.map((slot) => {
                    const key = JSON.stringify(slot);
                    const selected = formik.values.selectedSlot === key;
                    return <button type="button" key={key} aria-pressed={selected} className={`scheduling-slot-chip ${selected ? "selected" : ""}`}
                      onClick={() => formik.setFieldValue("selectedSlot", key)}>
                      {formatInZone(slot.startsAt, bookingZone, "h:mm A")}
                      {daySlots.some((other) => other.startsAt !== slot.startsAt && formatInZone(other.startsAt, bookingZone, "HH:mm") === formatInZone(slot.startsAt, bookingZone, "HH:mm")) &&
                        <small> {formatInZone(slot.startsAt, bookingZone, "[UTC]Z")}</small>}
                    </button>;
                  })}
                </div>
              </div>
            </div>
          </fieldset>
          {parsedSlot && <div className="schedule-booking-summary">
            <strong>{formatRange(parsedSlot.startsAt, parsedSlot.endsAt, bookingZone)} · {timeZoneLabel(bookingZone)}</strong>
            {selectedCategory && ` · ${selectedCategory.name} · ${selectedCategory.durationMinutes} min`}
            {isReschedule && !noPreference ? ` · ${practitioners.filter((person) => formik.values.practitionerRefs.includes(person.value)).map((person) => person.name).join(", ")}` : formik.values.practitionerRef ? ` · ${practitioners.find((person) => person.value === formik.values.practitionerRef)?.name || "Selected practitioner"}` : " · Any available practitioner"}
          </div>}
          {isReschedule ? <AppointmentVisitDetails appointment={appointment} encounter={appointmentEncounter} loading={encounterLoading} error={encounterError}
            onReload={onEncounterReload} onEdit={setEditingVisitDetails} disabled={btnLoading} /> : <fieldset className="schedule-booking-details" disabled={!!pendingBooking || btnLoading} style={{ minWidth: 0 }}>
            <TextInput keyField="reason" label={isInternalReferral ? "Reason for referral" : "Reason for visit"} formik={formik}
              placeholder="Briefly describe the reason" disabled={type === "reschedule" || btnLoading} />
            <div className="schedule-field-grid">
              <SelectFiled keyField="preferredChannel" label="Appointment by" formik={formik}
                options={[{ name: "Phone", value: "phone" }, { name: "Video / chat", value: "video/chat" }]} />
              <BookingPhoneInput value={formik.values.phone} onChange={(phone) => formik.setFieldValue("phone", phone)}
                onBlur={formik.handleBlur} error={formik.touched.phone ? formik.errors.phone : undefined} disabled={btnLoading} />
              <TextInput keyField="ext" label="Extension" formik={formik} required={false} inputMode="numeric" autoComplete="tel-extension"
                onChange={(event) => formik.setFieldValue("ext", event.target.value.replace(/\D/g, ""))} disabled={btnLoading} />
            </div>
            <fieldset className="schedule-end-choice">
              <legend>Appointment notifications</legend>
              <div className="schedule-end-options">
                {NOTIFICATION_DESTINATIONS.map((option) => (
                  <label key={option.value} className="schedule-end-option">
                    <input type="radio" name="smsNotificationDestination" value={option.value} checked={formik.values.smsNotificationDestination === option.value}
                      onChange={() => formik.setFieldValue("smsNotificationDestination", option.value)} disabled={btnLoading} />
                    <span>{option.label}</span>
                  </label>
                ))}
              </div>
              <p className="schedule-help">{notificationDestinationHelp({ destination: formik.values.smsNotificationDestination, returnPhone: formik.values.phone, patientPhone })}</p>
            </fieldset>
          </fieldset>}
          {hold && <p className="schedule-booking-summary" role="status">This time is reserved for you for {Math.floor(holdSeconds / 60)}:{String(holdSeconds % 60).padStart(2, "0")}. Complete the booking before it expires.</p>}
          {saveError && <p className="schedule-error" role="alert">{saveError}</p>}
        </form>
      </Modal.Body>
      <Modal.Footer className={`schedule-dialog-footer${isReschedule ? " appointment-detail-actions" : ""}`}>
        <Button variant="secondary" onClick={handleClose} disabled={btnLoading || holdLoading}>{pendingBooking ? "Close" : "Cancel"}</Button>
        {type !== "reschedule" && !pendingBooking && <Button variant="secondary" onClick={hold ? dropHold : takeHold} isLoading={holdLoading} disabled={!parsedSlot || btnLoading}>
          {hold ? "Release reserved time" : "Reserve for 5 minutes"}
        </Button>}
        {isReschedule ? <>
          <Button onClick={() => submitBooking(false)} isLoading={btnLoading} disabled={!parsedSlot || holdLoading || slotsLoading || !rescheduleChanged || hasLinkedEncounter}>
            Reschedule
          </Button>
          <Button variant="secondary" onClick={() => submitBooking(true)} isLoading={btnLoading} disabled={!parsedSlot || holdLoading || slotsLoading || !rescheduleChanged || hasLinkedEncounter}>
            Block and reschedule
          </Button>
        </> : <Button onClick={submitBooking} isLoading={btnLoading} disabled={(!parsedSlot && !pendingBooking) || holdLoading || slotsLoading}>
          {pendingBooking ? (referralContext ? "Retry referral" : "Retry linking") : "Book appointment"}
        </Button>}
      </Modal.Footer>
    </Modal>
    <AppointmentVisitEditor field={editingVisitDetails} appointment={appointment} organizationRef={resolvedOrgId} encounter={appointmentEncounter} onHide={() => setEditingVisitDetails("")} onSaved={onEncounterReload} />
    </>
  );
};

export default BookingModal;
